import { useCallback, useEffect, useRef } from "react";
import { useStores } from "../../context/StoresContext";
import { useStoreOperations } from "./useStoreOperations";
import { useProductOperations } from "./useProductOperations";
import { VALID_TABS } from "../../const/inventario";
import { UseAdmin } from "../UseAdmin";

/**
 * Gestionar el cambio de tabs en inventario.
 * Sincroniza el estado al cambiar de tab.
 */
export const useInventarioTabManager = (activeTab) => {
  const { stores, selectedStoreData, setSelectedStoreData } = useStores();
  const { states: storeStates } = useStoreOperations();
  const { operations: productOps, states: productStates } = useProductOperations();
  const { brandfcNombre: brand } = UseAdmin();

  const hasInitializedRef = useRef(false);

  /**
   * Valida si el almacén actual es válido y accesible
   */
  const isValidStore = useCallback(
    (store) => {
      if (!store) return false;

      if (store.name === "Todos") return true;

      return stores.some((s) => s.id === store.id);
    },
    [stores]
  );

  /**
   * Obtiene el almacén "Todos" del contexto
   */
  const resetToTodos = useCallback(async () => {
    const todosStore = stores.find((s) => s.name === "Todos");

    if (!todosStore) return;

    setSelectedStoreData(todosStore);
    await productOps.fetchProducts();
  }, [stores, setSelectedStoreData, productOps]);

  useEffect(() => {
    if (!brand) {
      // eslint-disable-next-line no-console
      console.warn("⏳ [useInventarioTabManager]Esperando cargar marca...");
      return;
    }

    if (storeStates.fetchState.loading) return;
    if (stores.length === 0) return;

    const tabByShowProducts = VALID_TABS.filter((tab) => tab.showProducts).map((tab) => tab.name);

    if (!tabByShowProducts.includes(activeTab)) {
      return;
    }

    if (hasInitializedRef.current) return;

    hasInitializedRef.current = true;

    // Si hay almacén guardado en sessionStorage, validarlo
    if (selectedStoreData) {
      if (!isValidStore(selectedStoreData)) {
        console.warn("⚠️ Almacén guardado no válido, reseteando a Todos");
        resetToTodos();
        return;
      }

      // Cargar productos para el almacén guardado
      if (selectedStoreData.name === "Todos") {
        productOps.fetchProducts();
      } else {
        productOps.fetchProductsByStore(selectedStoreData.id);
      }
    } else {
      // Sin almacén seleccionado, resetear a "Todos"
      resetToTodos();
    }
  }, [
    brand,
    storeStates.fetchState.loading,
    stores,
    selectedStoreData,
    activeTab,
    isValidStore,
    resetToTodos,
    productOps,
  ]);

  const reloadCurrentStore = useCallback(async () => {
    if (!selectedStoreData) return;
    if (selectedStoreData.name === "Todos") {
      await productOps.fetchProducts();
    } else {
      await productOps.fetchProductsByStore(selectedStoreData.id);
    }
  }, [selectedStoreData, productOps]);

  return {
    isValidStore,
    resetToTodos,
    reloadCurrentStore,
    isLoading: productStates.fetchState.loading || !brand, // Cargar hasta tener brand
  };
};
